import { isInlineChannelDeleteError } from "./features/channels/errors";
import { isInlineContactCreateError, isInlineContactMergeError } from "./features/contacts/errors";
import { lazy, Suspense, useEffect, useMemo, useRef } from "react";
import {
  isAiSummaryMiss,
  isBackgroundPlanDenial,
  trackUserGestures,
} from "./api/background-errors";
import { Route, Routes } from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { createQueryClient } from "./api/query-client";
import { DEFAULT_LOCALE, type Locale } from "@wato/i18n";
import { I18nProvider, useI18n } from "@wato/i18n/react";
import { GoogleRootFlagToast } from "./features/settings/integrations/google-result/GoogleRootFlagToast";
import { ToastProvider, useToast } from "@wato/ui";
import { createHttpApiClient, type ApiClient } from "./api/client";
import { ApiProvider, useErrorMessage } from "./api/context";
import { AuthProvider, SESSION_EXPIRED_EVENT } from "./auth/AuthProvider";
import { GuestOnly, RequireAuth, SETUP_ROUTE } from "./auth/guards";
import { PageLoading } from "./components/common";
import { NotFoundPage } from "./components/NotFoundPage";
import { LoginPage } from "./features/auth/LoginPage";
import { ConfirmEmailPage } from "./features/auth/ConfirmEmailPage";
import { ResetPasswordPage } from "./features/auth/ResetPasswordPage";
import { SignupPage } from "./features/auth/SignupPage";
import { PhoneOtpLogin } from "./pages/login/PhoneOtpLogin";
import { TwoFactorLoginPage } from "./features/auth/TwoFactorLoginPage";
import { TwoFactorSetupPage } from "./features/auth/TwoFactorSetupPage";
import { VerifyEmailPage } from "./features/auth/VerifyEmailPage";
import { WorkspaceEntry } from "./features/onboarding/OnboardingEntry";
import {
  WORKSPACELESS_PREFIXES,
  WorkspacelessRedirect,
} from "./features/onboarding/WorkspacelessRedirect";
import { WorkspaceShell } from "./features/workspace/WorkspaceShell";

import { workspaceCoreRoutes } from "./app-routes/workspaceCoreRoutes";
import { workspaceSalesRoutes } from "./app-routes/workspaceSalesRoutes";
import { workspaceStoreRoutes } from "./app-routes/workspaceStoreRoutes";
import { workspaceBookingsRoutes } from "./app-routes/workspaceBookingsRoutes";
import { workspaceAiRoutes } from "./app-routes/workspaceAiRoutes";
import { workspaceMiscRoutes } from "./app-routes/workspaceMiscRoutes";
import { workspaceSettingsRoutes } from "./app-routes/workspaceSettingsRoutes";

export interface AppProps {
  /** WP-106 wiring point: the signed-in user's `User.locale`. */
  readonly initialLocale?: Locale;
  /** API client; default: `fetch` against the same origin. Tests inject an in-memory one. */
  readonly api?: ApiClient;
}

// Route-level code splitting: only auth + shell + home stay in the entry chunk.
const ConsentScreen = lazy(() =>
  import("./features/settings/integrations/connected-apps/ConsentScreen").then((m) => ({
    default: m.ConsentScreen,
  })),
);
const DeveloperConsole = lazy(() =>
  import("./features/settings/integrations/connected-apps/DeveloperConsole").then((m) => ({
    default: m.DeveloperConsole,
  })),
);
const AddPhoneLogin = lazy(() =>
  import("./pages/settings/account/AddPhoneLogin").then((m) => ({ default: m.AddPhoneLogin })),
);
const CreateWorkspacePage = lazy(() =>
  import("./features/onboarding/CreateWorkspacePage").then((m) => ({
    default: m.CreateWorkspacePage,
  })),
);
const SetupWizardRoute = lazy(() =>
  import("./features/onboarding-wizard").then((m) => ({ default: m.SetupWizardRoute })),
);
const AcceptInvitePage = lazy(() =>
  import("./features/invites/AcceptInvitePage").then((m) => ({ default: m.AcceptInvitePage })),
);
const FlowEditorRoute = lazy(() =>
  import("./features/flows/FlowEditorRoute").then((m) => ({ default: m.FlowEditorRoute })),
);
const ProfilePage = lazy(() =>
  import("./features/account/ProfilePage").then((m) => ({ default: m.ProfilePage })),
);
const SallaInstallErrorPage = lazy(() =>
  import("./features/apps/salla-onboarding").then((m) => ({ default: m.SallaInstallErrorPage })),
);
const SallaLinkPage = lazy(() =>
  import("./features/apps/salla-onboarding").then((m) => ({ default: m.SallaLinkPage })),
);
const SallaOnboardingEntry = lazy(() =>
  import("./features/apps/salla-onboarding").then((m) => ({ default: m.SallaOnboardingEntry })),
);
const SallaListingSupportPage = lazy(() =>
  import("./features/apps/salla-listing").then((m) => ({ default: m.SallaListingSupportPage })),
);
const ChangelogPage = lazy(() =>
  import("./pages/changelog").then((m) => ({ default: m.ChangelogPage })),
);
export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoading />}>
      <GoogleRootFlagToast />
      <Routes>
        <Route element={<GuestOnly />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/phone" element={<PhoneOtpLogin />} />
        </Route>
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/login/2fa" element={<TwoFactorLoginPage />} />
        <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/confirm-email" element={<ConfirmEmailPage />} />
        <Route path={SETUP_ROUTE} element={<TwoFactorSetupPage />} />
        <Route element={<RequireAuth />}>
          <Route path="/" element={<WorkspaceEntry target="home" />} />
          <Route path="/onboarding" element={<WorkspaceEntry target="onboarding" />} />
          {WORKSPACELESS_PREFIXES.flatMap((p) => [
            <Route key={p} path={`/${p}`} element={<WorkspacelessRedirect />} />,
            <Route key={`${p}/*`} path={`/${p}/*`} element={<WorkspacelessRedirect />} />,
          ])}
          <Route path="/account" element={<ProfilePage />} />
          <Route path="/account/phone-login" element={<AddPhoneLogin />} />
          <Route path="/workspaces/new" element={<CreateWorkspacePage />} />
          <Route path="/apps/salla/onboarding" element={<SallaOnboardingEntry />} />
          <Route path="/apps/salla/link" element={<SallaLinkPage />} />
          <Route path="/oauth/consent" element={<ConsentScreen />} />
          <Route path="/developer/apps" element={<DeveloperConsole />} />
          <Route path="/w/:workspaceId/flows/:flowId/edit" element={<FlowEditorRoute />} />
          <Route
            path="/w/:workspaceId/flows/:flowId/try"
            element={<FlowEditorRoute view="try" />}
          />
          <Route path="/w/:workspaceId/onboarding" element={<SetupWizardRoute />} />
          <Route path="/w/:workspaceId" element={<WorkspaceShell />}>
            {workspaceCoreRoutes}
            {workspaceSalesRoutes}
            {workspaceStoreRoutes}
            {workspaceBookingsRoutes}
            {workspaceAiRoutes}
            {workspaceMiscRoutes}
            {workspaceSettingsRoutes}
          </Route>
        </Route>
        {/* Public: the invite token is the credential; the page handles signed-in state. */}
        <Route path="/invite" element={<AcceptInvitePage />} />
        {/* WP-329: the Salla install callback failed before any session existed. */}
        <Route path="/apps/salla/error" element={<SallaInstallErrorPage />} />
        {/* WP-330: Salla listing review — data handling, scopes, support and privacy links. */}
        <Route path="/apps/salla/support" element={<SallaListingSupportPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function DefaultApi({ children }: { children: React.ReactNode }) {
  const { locale } = useI18n();
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const toast = useToast();
  const errorMessage = useErrorMessage();
  useEffect(() => trackUserGestures(), []);
  const notifyRef = useRef((_error: unknown) => {});
  notifyRef.current = (error) => toast.show({ tone: "error", title: errorMessage(error) });
  const client = useMemo(
    () =>
      // Same origin: `/v1/*` is proxied to the API (Vite dev server, ingress in prod), so
      // the HttpOnly session cookie and the API's Origin check both just work.
      createHttpApiClient({
        locale: () => localeRef.current,
        // FB3-47: a failed write always shows an Arabic/English message, even where the
        // screen itself swallows the error. Sign-in/2FA screens report their own errors inline.
        onMutationError: (error, request) => {
          const { path } = request;
          if (
            isInlineContactCreateError(error, request) ||
            isInlineContactMergeError(request) ||
            isInlineChannelDeleteError(error, request)
          )
            return;
          // OWN-89: a plan-gated call made in the background never toasts on page load.
          if (isBackgroundPlanDenial(error)) return;
          // The panel's AI-summary card shows a missing summary itself.
          if (isAiSummaryMiss(error, request)) return;
          if (!path.startsWith("/v1/auth") && error.status !== 401) notifyRef.current(error);
        },
        // Q4-XCT-02: the first 401 ends the session in the UI: message + redirect to login.
        onUnauthorized: (error) => {
          notifyRef.current(error);
          window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
        },
      }),
    [],
  );
  return <ApiProvider client={client}>{children}</ApiProvider>;
}

export function App({ initialLocale = DEFAULT_LOCALE, api }: AppProps) {
  const queryClient = useMemo(() => createQueryClient(), []);
  const routes = (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </QueryClientProvider>
  );
  return (
    <I18nProvider initialLocale={initialLocale} persistKey="wato.locale" titleKey="dashboard.title">
      <ToastProvider maxVisible={1}>
        {api ? <ApiProvider client={api}>{routes}</ApiProvider> : <DefaultApi>{routes}</DefaultApi>}
      </ToastProvider>
    </I18nProvider>
  );
}
